import * as THREE from 'three';
import * as TSL from 'three/tsl';
import { MeshBasicNodeMaterial } from 'three/webgpu';

import { EngineClass } from '../../../systems/ClassRegistry.js';
import { property } from '../../../utils/serialization/decorator.js';
import { ISerializableObjectTag } from '../../../utils/serialization/serializable.js';

import { NodeMaterialAsset } from './NodeMaterialAsset.js';

import type { IDumper, ILoader } from '../../../utils/serialization/factories.js';

/** Animated heat-haze UV distortion with cool/hot tint on `MeshBasicNodeMaterial`. */
@EngineClass('HeatDistortionNodeMaterialAsset', {
  isNodeMaterialAsset: true,
  nodeMaterialDisplayName: 'Heat Distortion',
  nodeMaterialGroup: 'TSL Node FX',
})
export class HeatDistortionNodeMaterialAsset extends NodeMaterialAsset(MeshBasicNodeMaterial) {
  readonly [ISerializableObjectTag] = true as const;

  @property({ type: 'color', description: 'Cool color' })
    colorCool = new THREE.Color(0.0, 0.2, 1.0);

  @property({ type: 'color', description: 'Hot color' })
    colorHot = new THREE.Color(1.0, 0.1, 0.0);

  @property({ type: 'number', min: 0.01, max: 10.0, step: 0.01, description: 'Animation speed' })
    speed = 1.5;

  @property({ type: 'number', min: 1, max: 30, step: 0.5, description: 'Distortion wave frequency' })
    waveFrequency = 8.0;

  @property({ type: 'number', min: 0.001, max: 0.5, step: 0.001, description: 'Distortion amplitude' })
    waveAmplitude = 0.07;

  private readonly _speedUniform = TSL.uniform(this.speed);
  private readonly _waveFrequencyUniform = TSL.uniform(this.waveFrequency);
  private readonly _waveAmplitudeUniform = TSL.uniform(this.waveAmplitude);

  constructor() {
    super();
    this.rebuild();
  }

  override rebuild(): void {
    this.applyCommonMaterialState();
    this.speed = this.clampFiniteNumber(this.speed, 0.01, 10.0, 1.5);
    this.waveFrequency = this.clampFiniteNumber(this.waveFrequency, 1, 30, 8.0);
    this.waveAmplitude = this.clampFiniteNumber(this.waveAmplitude, 0.001, 0.5, 0.07);
    this._speedUniform.value = this.speed;
    this._waveFrequencyUniform.value = this.waveFrequency;
    this._waveAmplitudeUniform.value = this.waveAmplitude;

    const t = TSL.time.mul(this._speedUniform);
    const nx = TSL.sin(TSL.positionLocal.y.mul(this._waveFrequencyUniform).add(t)).mul(this._waveAmplitudeUniform);
    const ny = TSL.cos(TSL.positionLocal.x.mul(this._waveFrequencyUniform).add(t.mul(TSL.float(1.3)))).mul(this._waveAmplitudeUniform);
    const displaced = TSL.Fn(() => {
      const p = TSL.positionLocal.toVar();
      p.x.addAssign(nx);
      p.y.addAssign(ny);
      return p;
    })();
    const heat = TSL.sin(displaced.y.mul(TSL.float(5)).add(t)).mul(TSL.float(0.5)).add(TSL.float(0.5));
    const coolNode = TSL.color(this.colorCool);
    const hotNode = TSL.color(this.colorHot);
    this.colorNode = TSL.mix(coolNode, hotNode, heat);
    this.positionNode = displaced;
  }

  public serialize(dumper: IDumper): void {
    this.serializeAuthoredFields(dumper);
  }

  public static staticDeserialize(data: unknown, loader: ILoader): HeatDistortionNodeMaterialAsset {
    const instance = new HeatDistortionNodeMaterialAsset();
    HeatDistortionNodeMaterialAsset.loadAuthoredFields(instance, loader);
    instance.rebuild();
    return instance;
  }
}
